'use client';

import React from 'react';
import Link from 'next/link';
import { formatPrice, getWhatsAppUrl } from '@/lib/utils';

export interface ProductCardProps {
  id: number;
  name: string;
  slug: string;
  description: string;
  price: number | string;
  comparePrice?: number | string | null;
  franchise: string;
  featured: boolean;
  isNew: boolean;
  images: { url: string; alt: string }[];
  category?: { name: string; slug: string };
}

export function ProductCard({
  name,
  slug,
  description,
  price,
  comparePrice,
  franchise,
  featured,
  isNew,
  images,
  category,
}: ProductCardProps): React.JSX.Element {
  const imageUrl =
    images && images.length > 0
      ? images[0].url
      : 'https://images.unsplash.com/photo-1578632767115-351597cf2477?w=400&auto=format&fit=crop&q=80';

  return (
    <article
      className={`group relative flex flex-col justify-between rounded-xl overflow-hidden bg-[#12111F]/90 border transition-all duration-200 active:scale-[0.98] ${
        featured
          ? 'border-red-500/50 shadow-sm'
          : 'border-[#2A2545]'
      }`}
    >
      {/* Badges superiores adaptados a celulares */}
      <div className="absolute top-2 left-2 z-10 flex flex-col gap-1 pointer-events-none">
        {featured && (
          <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-600 text-white shadow-md">
            KAIO-KEN
          </span>
        )}
        {isNew && (
          <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-400 text-black shadow-md">
            NUEVO
          </span>
        )}
      </div>

      {/* Franquicia Badge */}
      <div className="absolute top-2 right-2 z-10 pointer-events-none">
        <span className="text-[9px] font-medium px-2 py-0.5 rounded-full bg-black/75 backdrop-blur-sm text-slate-200 border border-white/10">
          {franchise}
        </span>
      </div>

      {/* Imagen del Producto */}
      <Link href={`/producto/${slug}`} className="block relative aspect-[4/3] w-full overflow-hidden bg-[#0A0A12]">
        <img
          src={imageUrl}
          alt={name}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#12111F] via-transparent to-transparent opacity-60" />
      </Link>

      {/* Información del Producto optimizada para touch */}
      <div className="p-3 sm:p-4 flex-1 flex flex-col justify-between">
        <div>
          {category && (
            <span className="text-[10px] font-mono text-emerald-400 uppercase tracking-widest block mb-1">
              {category.name}
            </span>
          )}
          <Link href={`/producto/${slug}`}>
            <h3 className="text-xs sm:text-sm font-bold text-white line-clamp-1 group-hover:text-amber-400 transition-colors">
              {name}
            </h3>
          </Link>
          <p className="text-[11px] sm:text-xs text-slate-400 mt-1 line-clamp-2 leading-snug">
            {description}
          </p>
        </div>

        {/* Precio Scouter & Botón WhatsApp Táctil */}
        <div className="mt-3 pt-2.5 border-t border-[#2A2545]/60 flex items-center justify-between">
          <div>
            <div className="text-[9px] font-mono text-slate-500 uppercase tracking-wider">
              PODER / PRECIO
            </div>
            <div className="flex items-baseline space-x-1">
              <span className="text-sm sm:text-base font-black font-mono text-amber-400">
                {formatPrice(price)}
              </span>
              {comparePrice && (
                <span className="text-[10px] text-slate-500 line-through font-mono">
                  {formatPrice(comparePrice)}
                </span>
              )}
            </div>
          </div>

          <a
            href={getWhatsAppUrl(name)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Consultar por WhatsApp"
            className="p-2.5 rounded-lg bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 active:bg-emerald-500 active:text-black transition-colors"
          >
            💬
          </a>
        </div>
      </div>
    </article>
  );
}
